import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import { normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { IAllPageFilters, IPageType } from 'utils/types';

import { prepareFormData } from '../../utils/form';

import {
  normalizeCardMasks,
  normalizeCardPans,
  normalizeDevices,
  normalizeEmails,
  normalizeIpAddress,
} from './normalizers';
import {
  IBlacklistedEmail,
  IBlacklistedEmailData,
  ICardMasks,
  ICardMasksData,
  ICardMasksForm,
  ICardPans,
  ICardPansData,
  ICardPansForm,
  IDevice,
  IDeviceForm,
  IDevicesData,
  IEmailForm,
  IIpAddress,
  IIpAddressData,
  IIpAddressForm,
} from './types';

const blacklistsApi = {
  async getAllCardMasks(filters: IAllPageFilters): Promise<IPageType<ICardMasks>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('blacklists/card-masks', {
      params: normalizedFilter,
    });

    return normalizePageData<ICardMasksData, ICardMasks>(
      response.data,
      normalizeCardMasks,
      JSON.stringify(filters.search),
    );
  },

  async getAllCardPans(filters: IAllPageFilters): Promise<IPageType<ICardPans>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('blacklists/card', {
      params: normalizedFilter,
    });

    return normalizePageData<ICardPansData, ICardPans>(
      response.data,
      normalizeCardPans,
      JSON.stringify(filters.search),
    );
  },

  async createCardPans(payload: ICardPansForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const result = await axiosInstance.post(`blacklists/card`, normalizedPayload);
    return result;
  },

  async deleteCardPans(ids: number[]): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`blacklists/card`, {
      data: { value: ids },
    });

    return response.data;
  },

  async getAllEmails(filters: IAllPageFilters): Promise<IPageType<IBlacklistedEmail>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('blacklists/emails', {
      params: normalizedFilter,
    });

    return normalizePageData<IBlacklistedEmailData, IBlacklistedEmail>(
      response.data,
      normalizeEmails,
      JSON.stringify(filters.search),
    );
  },

  async deleteEmails(ids: number[]): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`blacklists/emails`, {
      data: { value: ids },
    });

    return response.data;
  },

  async deleteCardMasks(ids: number[]): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`blacklists/card-masks`, {
      data: { value: ids },
    });

    return response.data;
  },

  async deleteIpAddress(ids: number[]): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`blacklists/ip-address`, {
      data: { value: ids },
    });

    return response.data;
  },

  async createCardMasks(payload: ICardMasksForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const result = await axiosInstance.post(`blacklists/card-masks`, normalizedPayload);
    return result;
  },

  async createEmail(payload: IEmailForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const result = await axiosInstance.post(`blacklists/emails`, normalizedPayload);
    return result;
  },

  async createIpAddress(payload: IIpAddressForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const result = await axiosInstance.post(`blacklists/ip-address`, normalizedPayload);
    return result;
  },

  async getAllIpAddresses(filters: IAllPageFilters): Promise<IPageType<IIpAddress>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('blacklists/ip-address', {
      params: normalizedFilter,
    });

    return normalizePageData<IIpAddressData, IIpAddress>(
      response.data,
      normalizeIpAddress,
      JSON.stringify(filters.search),
    );
  },

  async getAllDevices(filters: IAllPageFilters): Promise<IPageType<IDevice>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('blacklists/devices', {
      params: normalizedFilter,
    });

    return normalizePageData<IDevicesData, IDevice>(
      response.data,
      normalizeDevices,
      JSON.stringify(filters.search),
    );
  },

  async createDevice(payload: IDeviceForm): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const result = await axiosInstance.post(`blacklists/devices`, normalizedPayload);
    return result;
  },

  async deleteDevices(ids: number[]): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`blacklists/devices`, {
      data: { value: ids },
    });

    return response.data;
  },
};

export default blacklistsApi;
